import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Database, AlertTriangle } from 'lucide-react';
import { OrderTimelineStockStatusDto } from '../../../../../types/orderTimeline';

interface TimelineStockStatusProps {
  stockStatus: OrderTimelineStockStatusDto[];
}

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return '–';
  const d = new Date(dateStr);
  return d.toLocaleDateString('nb-NO', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' +
    d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' });
};

export const TimelineStockStatus: React.FC<TimelineStockStatusProps> = ({ stockStatus }) => {
  const [expanded, setExpanded] = useState(false);

  const hasNegative = stockStatus.some(s => s.isNegative);

  return (
    <div className="mb-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Database className="w-4 h-4" />
        Nåværende lagerstatus ({stockStatus.length})
        {hasNegative && <AlertTriangle className="w-3.5 h-3.5 text-red-500" />}
      </button>

      {expanded && (
        <div className="overflow-x-auto">
          {stockStatus.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-4 bg-white rounded-lg border border-gray-200">
              Ingen lagersaldo funnet for varer i denne ordren
            </div>
          ) : (
            <table className="w-full text-xs border-collapse bg-white rounded-lg border border-gray-200">
              <thead>
                <tr className="bg-gray-50 text-gray-600">
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Varenr</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Beskrivelse</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Lokasjon</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Lager</th>
                  <th className="text-right px-2 py-1.5 font-medium border-b border-gray-200">Saldo</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Siste bevegelse</th>
                  <th className="text-left px-2 py-1.5 font-medium border-b border-gray-200">Oppdatert av</th>
                </tr>
              </thead>
              <tbody>
                {stockStatus.map((s, i) => (
                  <tr key={i} className={`border-b border-gray-50 hover:bg-gray-50 ${s.isNegative ? 'bg-red-50' : ''}`}>
                    <td className="px-2 py-1.5 font-mono font-medium text-gray-800">{s.itemNo}</td>
                    <td className="px-2 py-1.5 text-gray-600 max-w-[200px] truncate">{s.description1 || '–'}</td>
                    <td className="px-2 py-1.5 font-mono text-[10px] text-gray-600">{s.binCode}</td>
                    <td className="px-2 py-1.5 text-gray-500">{s.warehouseID || '–'}</td>
                    <td className={`px-2 py-1.5 text-right font-bold ${s.isNegative ? 'text-red-600' : 'text-gray-800'}`}>
                      {s.isNegative && <AlertTriangle className="w-3 h-3 inline mr-1 text-red-500" />}
                      {s.quantity}
                    </td>
                    <td className="px-2 py-1.5 text-gray-400">{formatDateTime(s.lastMovementDate)}</td>
                    <td className="px-2 py-1.5 text-gray-500">{s.updatedBy || '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};
